/**
 * Penjelasan rasio keuangan dalam kalimat biasa.
 *
 * BATAS YANG TIDAK BOLEH DILANGGAR: fungsi ini MENJELASKAN arti angka, tidak MENILAI
 * emitennya. Tidak ada "murah", "mahal", "bagus", "sehat", "menarik", "layak".
 * Menilai emiten = analisis atas efek = butuh izin Penasihat Investasi (POJK 13/2025).
 * Menjelaskan arti angka = edukasi (POJK 6/2026 Pasal 6 ayat 2).
 *
 * Kalimatnya template yang SAMA untuk semua emiten — hanya angkanya berganti. Itulah
 * yang membuatnya definisi-yang-diterapkan, bukan opini per emiten.
 *
 * Tes `penjelasanRasio.test.ts` melewatkan SETIAP kalimat yang bisa dihasilkan fungsi ini
 * ke `checkOutput()` milik worker. Menambah template baru tanpa lolos penjaga itu =
 * tes merah.
 *
 * Rencana: docs/plans/2026-08-23-penjelasan-angka-screener/
 */

export interface BarisRasio {
  pe: number | null;
  pbv: number | null;
  roa: number | null;
  roe: number | null;
  der: number | null;
  npm: number | null;
}

export type KunciRasio = 'pe' | 'pbv' | 'roa' | 'roe' | 'der' | 'npm';

export interface Penjelasan {
  kunci: KunciRasio;
  /** Singkatan yang tertera di tabel + artinya — pemula tidak tahu singkatannya. */
  judul: string;
  /** Kalimat lengkap dengan angka baris ini sudah tersisip. */
  kalimat: string;
  /** true saat nilainya negatif atau tidak tersedia — dipakai UI untuk menandai. */
  perluPerhatian: boolean;
}

/** 6.1 → '6,1' · 12.153 → '12,15'. Koma desimal, maksimal 2 angka di belakang. */
export function angkaID(v: number, digit = 2): string {
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: digit }).format(v);
}

const JUDUL: Record<KunciRasio, string> = {
  pe: 'P/E — Harga terhadap Laba',
  pbv: 'PBV — Harga terhadap Nilai Buku',
  roa: 'ROA — Laba atas Aset',
  roe: 'ROE — Laba atas Modal',
  der: 'DER — Utang terhadap Modal',
  npm: 'NPM — Margin Laba Bersih',
};

const URUTAN: KunciRasio[] = ['pe', 'pbv', 'roa', 'roe', 'der', 'npm'];

/** Kalimat untuk nilai ≥ 0. Nol ikut ke sini — nol bukan rugi. */
const POSITIF: Record<KunciRasio, (n: string) => string> = {
  pe: (n) => `Harga per lembar setara ${n}× laba setahun terakhir per lembar.`,
  pbv: (n) => `Harga per lembar ${n}× nilai buku per lembar.`,
  roa: (n) => `Dari tiap Rp 100 aset, dihasilkan laba Rp ${n} setahun.`,
  roe: (n) => `Dari tiap Rp 100 modal pemegang saham, dihasilkan laba Rp ${n} setahun.`,
  der: (n) => `Tiap Rp 1 modal sendiri didampingi Rp ${n} utang.`,
  npm: (n) => `Dari tiap Rp 100 penjualan, Rp ${n} tersisa sebagai laba bersih.`,
};

/** Kalimat untuk nilai < 0. Menerangkan KENAPA angkanya negatif — itu yang paling
 *  membingungkan pemula, dan diam soal itu berarti membiarkan bagian tersulit tanpa
 *  jawaban. Tetap menerangkan, bukan menilai. */
const NEGATIF: Record<KunciRasio, (nilai: number) => string> = {
  pe: (v) =>
    `Negatif (${angkaID(v)}). Perusahaan mencatat rugi pada periode ini, sehingga rasio harga terhadap laba tidak bermakna.`,
  pbv: (v) =>
    `Negatif (${angkaID(v)}). Nilai buku per lembar minus — kewajiban tercatat melebihi aset.`,
  roa: (v) =>
    `Minus ${angkaID(Math.abs(v))}. Dari tiap Rp 100 aset, tercatat rugi Rp ${angkaID(Math.abs(v))} setahun.`,
  roe: (v) =>
    `Minus ${angkaID(Math.abs(v))}. Dari tiap Rp 100 modal pemegang saham, tercatat rugi Rp ${angkaID(Math.abs(v))} setahun.`,
  der: (v) =>
    `Negatif (${angkaID(v)}). Muncul saat ekuitas minus; rasio utang terhadap modal tidak bisa dibaca seperti biasa.`,
  npm: (v) =>
    `Minus ${angkaID(Math.abs(v))}. Dari tiap Rp 100 penjualan, tercatat rugi Rp ${angkaID(Math.abs(v))}.`,
};

/** Data tidak ada di publikasi IDX. Tidak diisi sendiri, tidak dikarang, tidak dikosongkan. */
const TIDAK_TERSEDIA = 'Tidak tersedia di publikasi IDX untuk periode ini.';

export function penjelasanRasio(baris: BarisRasio): Penjelasan[] {
  return URUTAN.map((kunci) => {
    const nilai = baris[kunci];
    if (nilai === null || nilai === undefined || Number.isNaN(nilai)) {
      return { kunci, judul: JUDUL[kunci], kalimat: TIDAK_TERSEDIA, perluPerhatian: true };
    }
    if (nilai < 0) {
      return { kunci, judul: JUDUL[kunci], kalimat: NEGATIF[kunci](nilai), perluPerhatian: true };
    }
    return {
      kunci,
      judul: JUDUL[kunci],
      kalimat: POSITIF[kunci](angkaID(nilai)),
      perluPerhatian: false,
    };
  });
}
